<script>
  import { modal } from '../lib/modal.js';
  import { createEventDispatcher } from 'svelte';
  import { Wallet } from '../lib/icons/index.js';
  import { t } from '../i18n/index.js';
  import { companyInvoicesApi } from '../stores/api.js';
  import { todayCalendarDay } from '../lib/payment.js';
  import { formatEuro } from '../lib/labels.js';
  import {
    paymentRequestBody, changedResidual, METODI_EFFETTIVI, metodoEffettivoLabel,
  } from '../lib/payment-status.js';
  import ErrorBanner from './ErrorBanner.svelte';
  import InfoHint from './InfoHint.svelte';

  /**
   * Registra il pagamento di una fattura: data, importo (già il residuo) e una
   * nota. Usato dall'elenco e dal dettaglio. Il residuo che l'operatore vede
   * viene rimandato al server, che rifiuta se nel frattempo è cambiato; in quel
   * caso il residuo nuovo diventa la proposta e l'operatore conferma di nuovo.
   */
  export let companyId;
  /** Una riga con `id`, `invoiceNumber`, `direction` e `pagamento`. */
  export let invoice;

  const dispatch = createEventDispatcher();
  const today = todayCalendarDay();

  let dataPagamento = today;
  /** Il residuo con cui il server confronta la richiesta: quello mostrato, o quello nuovo confermato. */
  let residuoAtteso = invoice?.pagamento?.residuo ?? 0;
  let importo = residuoAtteso;
  let note = '';
  let metodo = '';
  let saving = false;
  let error = null;
  /** Il residuo nuovo trovato dal server, da far confermare; null finché non succede. */
  let changed = null;
  /** Saldata da qualcun altro mentre il dialogo era aperto: non resta niente da registrare. */
  let settledMeanwhile = false;

  $: incasso = invoice?.direction === 'outbound';

  /** Chi ha aperto il dialogo ricarica se la fattura è cambiata, anche non per mano nostra. */
  function close() {
    if (saving) return;
    if (settledMeanwhile) dispatch('refresh');
    else dispatch('cancel');
  }

  async function save() {
    saving = true;
    error = null;
    try {
      const result = await companyInvoicesApi.recordPayment(companyId, invoice.id, paymentRequestBody({
        dataPagamento, importo, note, residuoAtteso, metodo,
      }));
      dispatch('recorded', result);
    } catch (err) {
      const moved = changedResidual(err);
      if (moved?.settled) {
        // Residuo zero: un altro pagamento l'ha saldata. Niente da proporre —
        // un importo di zero il server lo rifiuta — solo da dirlo.
        settledMeanwhile = true;
        changed = null;
      } else if (moved) {
        // Nessun nuovo tentativo: il residuo nuovo diventa la proposta, e la
        // conferma resta all'operatore.
        changed = moved;
        residuoAtteso = moved.residuo;
        importo = moved.residuo;
      } else {
        error = err;
      }
    } finally {
      saving = false;
    }
  }
</script>

<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
<div class="modal-backdrop" on:click|self={close}>
  <div class="modal-content w-full max-w-lg p-6" role="dialog" aria-modal="true" aria-labelledby="record-payment-title" use:modal={{ onClose: close }}>
    <h2 id="record-payment-title" class="text-lg text-heading font-display mb-1 flex items-center gap-2">
      <Wallet size={20} /> {incasso ? $t('invoices.pagamento.dialog.titleIncasso') : $t('invoices.pagamento.dialog.title')}
    </h2>
    <p class="text-xs text-muted mb-3">{$t('invoices.pagamento.dialog.invoice', { numero: invoice.invoiceNumber || invoice.id })}</p>
    <p class="text-sm mb-2" style="color: var(--color-text-body);">
      {$t('invoices.pagamento.dialog.intro')}<InfoHint term="statoPagamento" />
    </p>
    {#if !settledMeanwhile}
      <!-- Hidden once settled meanwhile: residuoAtteso still holds the figure the operator saw. -->
      <p class="text-sm font-mono font-bold mb-4 text-heading">
        {$t('invoices.pagamento.dialog.residuo', { importo: formatEuro(residuoAtteso) })}
      </p>
    {/if}

    {#if changed}
      <div class="card p-4 mb-4 border-yellow-500" role="alert">
        <p class="text-sm" style="color: var(--color-text-body);">
          {$t('invoices.pagamento.dialog.changed', { importo: formatEuro(changed.residuo) })}
        </p>
      </div>
    {/if}

    <ErrorBanner {error} />

    {#if settledMeanwhile}
      <div class="card p-4 mb-4 border-green-500" role="status">
        <p class="text-sm" style="color: var(--color-text-body);">{$t('invoices.pagamento.dialog.settledMeanwhile')}</p>
      </div>
      <div class="flex justify-end pt-2">
        <button type="button" class="btn btn-primary btn-sm" on:click={close}>{$t('common.close')}</button>
      </div>
    {:else}
    <div class="space-y-4">
      <div>
        <label class="label" for="payment-date">{$t('invoices.pagamento.dialog.date')}</label>
        <input id="payment-date" type="date" class="input" bind:value={dataPagamento} max={today} />
        <p class="mt-1 text-xs text-muted">{$t('invoices.pagamento.dialog.dateHelp')}</p>
      </div>
      <div>
        <label class="label" for="payment-amount">{$t('invoices.pagamento.dialog.amount')}</label>
        <input id="payment-amount" type="number" step="0.01" min="0.01" class="input" bind:value={importo} />
        <p class="mt-1 text-xs text-muted">{$t('invoices.pagamento.dialog.amountHelp')}</p>
      </div>
      <div>
        <label class="label" for="payment-method">
          {$t('invoices.pagamento.metodo.label')}<InfoHint term="metodoEffettivo" />
        </label>
        <select id="payment-method" class="input" bind:value={metodo}>
          <option value="">{$t('invoices.pagamento.metodo.none')}</option>
          {#each METODI_EFFETTIVI as m}
            <option value={m}>{$t(metodoEffettivoLabel(m))}</option>
          {/each}
        </select>
        <p class="mt-1 text-xs text-muted">{$t('invoices.pagamento.metodo.help')}</p>
      </div>
      <div>
        <label class="label" for="payment-note">{$t('invoices.pagamento.dialog.note')}</label>
        <input id="payment-note" type="text" maxlength="500" class="input" bind:value={note}
          placeholder={$t('invoices.pagamento.dialog.notePlaceholder')} />
      </div>
      <div class="flex justify-end gap-3 pt-2">
        <button type="button" class="btn btn-secondary btn-sm" on:click={close} disabled={saving}>
          {$t('common.cancel')}
        </button>
        <button type="button" class="btn btn-primary btn-sm" on:click={save} disabled={saving || !dataPagamento}>
          {saving ? $t('invoices.pagamento.dialog.saving') : $t('invoices.pagamento.dialog.submit')}
        </button>
      </div>
    </div>
    {/if}
  </div>
</div>
